import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  deleteAudit,
  getAuditsByMissionId,
} from '../../redux/audits/auditActions'
import { AppDispatch, RootState, useAppDispatch } from '../../redux/store'
import { formatTitle } from '../../utils/formatTitle'
import { Audit } from '../../types/auditTypes'
import {
  DetailEntityPanel,
  DetailTable,
} from '../../components/common/detail'
import {
  canManageMission,
  DetailRowActions,
} from '../../components/common/detail/DetailRowActions'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import '../../components/common/ListPageTable.css'

type Props = {
  MissionId: string
  onCountChange?: (count: number) => void
}

function asAuditRows(raw: unknown): Audit[] {
  if (!raw) return []
  if (Array.isArray(raw)) {
    return raw.filter((a): a is Audit => Boolean(a && (a as Audit)._id))
  }
  if (typeof raw === 'object' && Array.isArray((raw as any).audits)) {
    return ((raw as any).audits as Audit[]).filter((a) => a && a._id)
  }
  return Object.values(raw as object).filter(
    (item): item is Audit =>
      Boolean(item && typeof item === 'object' && (item as Audit)._id)
  )
}

const AuditListByMission: React.FC<Props> = ({ MissionId, onCountChange }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch: AppDispatch = useAppDispatch()
  const roles = useSelector((state: any) => state.auth.roles)
  const loading = useSelector((state: RootState) => state.audit.loading)
  const auditsList = useSelector((state: RootState) => state.audit.audits)
  const [search, setSearch] = useState('')
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (MissionId) {
      setLoaded(false)
      dispatch(getAuditsByMissionId(MissionId)).finally(() => setLoaded(true))
    }
  }, [dispatch, MissionId])

  const all = useMemo(() => asAuditRows(auditsList), [auditsList])

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return all
    return all.filter(
      (a) =>
        (a.AuditName || '').toLowerCase().includes(q) ||
        (a.Description || '').toLowerCase().includes(q)
    )
  }, [all, search])

  useEffect(() => {
    onCountChange?.(all.length)
  }, [onCountChange, all.length])

  const canAdd = canManageMission(roles)

  return (
    <DetailEntityPanel
      listTitle={t('missionDetailsPage.sections.auditList', {
        defaultValue: 'Audits',
      })}
      searchPlaceholder={t('filterByName', { defaultValue: 'Filter by name' })}
      searchValue={search}
      onSearchChange={setSearch}
      addLabel={t('buttons.addAudit', { defaultValue: 'Add New Audit' })}
      onAdd={() => navigate('/addaudit')}
      canAdd={canAdd}
    >
      <DetailTable<Audit>
        loading={loading && !loaded}
        loadingLabel={t('loading')}
        emptyTitle={t('missionDetailsPage.empty.title', {
          defaultValue: 'Nothing here yet',
        })}
        emptyMessage={t('missionDetailsPage.empty.audits', {
          defaultValue: 'No audit is linked to this mission yet.',
        })}
        rows={rows}
        rowKey={(r) => r._id as string}
        columns={[
          {
            key: 'name',
            header: t('audit_name', { defaultValue: 'Audit name' }),
            render: (r) => (
              <NavLink
                to={`/audit-detail/${r._id}`}
                className="incyia-name-link"
              >
                <ListPageDescription
                  value={r.AuditName ? formatTitle(r.AuditName) : undefined}
                  tone="primary"
                />
              </NavLink>
            ),
          },
          {
            key: 'desc',
            header: t('Description'),
            render: (r) => <ListPageDescription value={r.Description} />,
          },
        ]}
        actionsHeader={t('Actions', { defaultValue: 'Action' })}
        renderActions={(r) => (
          <DetailRowActions
            roles={roles}
            onEdit={() => navigate(`/audit/${r._id}`)}
            onDelete={() => dispatch(deleteAudit(r._id as string))}
            deleteItem={{
              entityType: t('audits', { defaultValue: 'Audit' }),
              name: r.AuditName ? formatTitle(r.AuditName) : undefined,
              details: [
                {
                  label: t('deleteConfirm.fields.mission'),
                  value: r.MissionName
                    ? formatTitle(r.MissionName)
                    : '—',
                },
              ],
            }}
          />
        )}
      />
    </DetailEntityPanel>
  )
}

export default AuditListByMission
